<script setup lang="ts">
// components
import CiModalFooter from '@/components/CiModal/CiModalFooter.vue';
import CiModalHeader from '@/components/CiModal/CiModalHeader.vue';

/********************
 * PROPS & EMITS     *
 ********************/
export interface CiModalErrorProps {
  buttons?: any;
  closeable?: boolean;
  errorMessage?: string | null;
  title?: string | null;
  warnings?: any;
}
const props = withDefaults(defineProps<CiModalErrorProps>(), {
  buttons: () => [],
  closeable: true,
  errorMessage: null,
  title: null,
  warnings: () => [],
});

const emit = defineEmits<{
  close: [];
}>();

/********************
 * COMPOSITIONS      *
 ********************/
const { $gettext } = useGettext();

/********************
 * REFS & VARS       *
 ********************/
const errorMessage = computed(() => {
  if (props.errorMessage) {
    return props.errorMessage;
  }
  return $gettext('Something went wrong. Please try again.');
});
</script>

<template>
  <div class="rounded bg-white shadow-2xl">
    <CiModalHeader
      :title="props.title"
      :closeable="props.closeable"
      @modal-close="closeable ? emit('close') : false"
    />
    <div class="container-fluid mt-4 px-4 py-6">
      <CiError :message="errorMessage" />
      <template
        v-if="warnings && warnings.length"
      >
        <div class="my-1 text-center text-warning">
          <span class="font-medium">{{ $gettext('Warning!') }}</span>
        </div>
        <div
          v-for="(warning, index) in warnings"
          :key="`${index}-${warning}`"
          class="mb-2 text-center text-warning"
        >
          {{ warning }}
        </div>
      </template>
    </div>
    <CiModalFooter
      v-if="buttons && buttons.length"
      class="flex justify-center p-4"
    >
      <div
        v-for="(button, index) in buttons"
        :key="`${index}-${button.label}`"
      >
        <CiButton
          v-if="button.to"
          tag="NuxtLink"
          :variant="button.variant"
          :class="button.classes"
          :href="button.to"
        >
          {{ button.label }}
        </CiButton>
        <CiButton
          v-if="button.onClick"
          tag="button"
          :variant="button.variant"
          :class="button.classes"
          @click="button.onClick"
        >
          {{ button.label }}
        </CiButton>
      </div>
    </CiModalFooter>
  </div>
</template>
